import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskLines } from "../components/Reveal";
import { DESTINATIONS } from "../data/content";
import { REGIONS, COUNTRIES, WORLD_PLACE_COUNT } from "../data/world";

const heroCountry = COUNTRIES.find((c) => c.slug === "italy") || COUNTRIES[0];

export default function DestinationsHub() {
  const [activeRegion, setActiveRegion] = useState("All");
  const cardImg = (c) => c.heroImg;
  const shownRegions = activeRegion === "All" ? REGIONS : REGIONS.filter((r) => r.name === activeRegion);

  return (
    <main data-testid="destinations-hub" className="bg-ivory">
      {/* Hero */}
      <section className="relative h-[82svh] overflow-hidden bg-navy">
        <img src={cardImg(heroCountry)} alt="Destinations" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.62)" }} />
        <div className="relative h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-[12vh]">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }} className="overline text-gold mb-6">{COUNTRIES.length} Countries · {WORLD_PLACE_COUNT}+ Places · One World, Curated</motion.p>
          <h1 className="font-serif text-ivory text-6xl md:text-8xl leading-[0.95]">
            <MaskLines lines={["The world,", "end to end."]} delay={0.3} />
          </h1>
        </div>
      </section>

      {/* Flagship destinations */}
      <section className="bg-ivory py-24 md:py-32" data-testid="hub-flagship">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal className="mb-14"><p className="overline text-charcoal/50">Signature Journeys</p>
            <h2 className="font-serif text-4xl md:text-6xl text-navy mt-5">Flagship destinations.</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DESTINATIONS.map((d, idx) => (
              <Reveal key={d.slug} delay={(idx % 4) * 0.05}>
                <Link to={`/destinations/${d.slug}`} className="group block" data-testid={`hub-dest-${d.slug}`}>
                  <div className="relative h-[300px] overflow-hidden grain">
                    <img src={d.thumb} alt={d.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(13,16,56,0.7), transparent 55%)" }} />
                    <div className="absolute bottom-0 p-5">
                      <p className="overline text-gold/80">{d.region}</p>
                      <h3 className="font-serif text-2xl text-ivory mt-1">{d.name}</h3>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Region filter chips */}
      <section className="bg-ivory pt-4 pb-2 sticky top-[64px] z-30" data-testid="region-filter-bar">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap gap-2.5 py-4 border-y border-navy/10">
            {["All", ...REGIONS.map((r) => r.name)].map((name) => (
              <button
                key={name}
                onClick={() => setActiveRegion(name)}
                data-testid={`region-chip-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                  activeRegion === name
                    ? "bg-navy text-ivory border-navy"
                    : "border-navy/20 text-charcoal/70 hover:border-gold hover:text-gold"
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Every region & country */}
      {shownRegions.map((region, ri) => (
        <section
          key={region.slug}
          className={`${ri % 2 === 0 ? "bg-navy grain" : "bg-ivory"} py-24 md:py-32`}
          data-testid={`hub-region-${region.slug}`}
        >
          <div className="max-w-[1500px] mx-auto px-6 md:px-10">
            <Reveal className="mb-14">
              <p className={`overline ${ri % 2 === 0 ? "text-gold" : "text-charcoal/50"}`}>Region · {region.name}</p>
              <h2 className={`font-serif text-4xl md:text-6xl mt-5 ${ri % 2 === 0 ? "text-ivory" : "text-navy"}`}>{region.countries.length} {region.name === "India" ? "destinations" : region.countries.length === 1 ? "country" : "countries"}, curated.</h2>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
              {region.countries.map((c, idx) => (
                <Reveal key={c.slug} delay={(idx % 5) * 0.05}>
                  <Link to={`/destinations/country/${c.slug}`} className="group block" data-testid={`hub-country-${c.slug}`}>
                    <div className="relative h-[340px] overflow-hidden grain">
                      <img src={cardImg(c)} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(13,16,56,0.85), transparent 55%)" }} />
                      <div className="absolute bottom-0 p-5">
                        <h3 className="font-serif text-2xl md:text-3xl text-ivory leading-tight">{c.name}</h3>
                        <p className="text-ivory/60 text-xs mt-2">{c.places.length} destinations</p>
                        <span className="inline-flex items-center gap-1 text-gold overline mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Explore <ArrowUpRight size={12} /></span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="relative py-28 md:py-40 overflow-hidden bg-navy">
        <img src={COUNTRIES[3].heroImg} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.74)" }} />
        <div className="relative max-w-[1500px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <h2 className="font-serif text-ivory text-5xl md:text-7xl max-w-[20ch] mx-auto leading-[1.05]">Cannot find your dream destination?</h2>
            <p className="text-ivory/70 max-w-xl mx-auto mt-6 leading-relaxed">Wherever it lies on the map, our curators will design the journey. Simply tell us where.</p>
            <Link to="/enquire?type=prive" className="btn-gold mt-10" data-testid="hub-cta">Begin Your Bespoke Journey <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
